import React from "react"
import Badge from "../common/Badge.jsx"
import TableWithPagination from "../table/TableWithPagination.jsx"
import { getManifestStatusBadgeStyle } from "../utils/manifestStatusBadge.js"

function renderTextCell(value, className = "", style = {}) {
    const displayValue = value ?? ""
    return (
        <td style={style} className={className}>
            <span
                title={displayValue}
                style={{
                    display: "block",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                }}
            >
                {displayValue}
            </span>
        </td>
    )
}

function renderComponentCell(content) {
    return (
        <td
            style={{
                whiteSpace: "nowrap",
                width: "1%",
            }}
        >
            {content}
        </td>
    )
}

function hexToRgba(hex, alpha) {
    const raw = String(hex ?? "").replace("#", "").trim()
    if (raw.length !== 6) {
        return `rgba(0, 0, 0, ${alpha})`
    }
    const r = parseInt(raw.slice(0, 2), 16)
    const g = parseInt(raw.slice(2, 4), 16)
    const b = parseInt(raw.slice(4, 6), 16)
    return `rgba(${r}, ${g}, ${b}, ${alpha})`
}

/**
 * @param {{
 *   manifests: any[],
 *   isLoadingInitial?: boolean,
 *   totalRows?: number,
 *   page?: number,
 *   perPage?: number,
 *   onPageChange?: (nextPage: number) => void,
 *   onPerPageChange?: (nextPerPage: number | "fit") => void,
 *   perPageSelection?: number | "fit",
 *   onFitPerPageChange?: (nextFitPerPage: number) => void,
 *   onManifestClick?: (manifest: any) => void,
 *   showRefetchLoader?: boolean,
 *   manifestStatusColor?: (status: any) => string,
 *   onSelectionChange?: (selectedManifests: any[]) => void,
 *   columns?: Array<{
 *     key: string,
 *     label: any,
 *     preferredWidth?: number,
 *     minWidth?: number,
 *     cellType?: "text" | "status" | "custom",
 *     value?: (manifest: any) => any,
 *     render?: (manifest: any) => any,
 *     headerClassName?: string,
 *     headerStyle?: any,
 *     cellClassName?: string,
 *     cellStyle?: any,
 *   }>,
 * }} props
 */
export default function ManifestsDesktopTable({
    manifests,
    isLoadingInitial = false,
    totalRows = 0,
    page = 1,
    perPage = 25,
    onPageChange,
    onPerPageChange,
    perPageSelection = undefined,
    onFitPerPageChange = undefined,
    onManifestClick = () => {},
    showRefetchLoader = false,
    manifestStatusColor = undefined,
    onSelectionChange = undefined,
    columns = undefined,
}) {
    const getManifestKey = React.useCallback((manifest, index) => {
        return manifest?.id ?? manifest?.manifestId ?? manifest?.manifestNumber ?? index
    }, [])

    const [selectedManifestKeys, setSelectedManifestKeys] = React.useState(
        /** @returns {any[]} */ () => []
    )

    React.useEffect(() => {
        const availableKeys = new Set(manifests.map(getManifestKey))
        setSelectedManifestKeys((prev) => prev.filter((key) => availableKeys.has(key)))
    }, [manifests, getManifestKey])

    const selectedManifestKeySet = React.useMemo(
        () => new Set(selectedManifestKeys),
        [selectedManifestKeys]
    )
    const selectedManifests = React.useMemo(
        () => manifests.filter((m, index) => selectedManifestKeySet.has(getManifestKey(m, index))),
        [manifests, selectedManifestKeySet, getManifestKey]
    )

    React.useEffect(() => {
        if (typeof onSelectionChange !== "function") {
            return
        }
        onSelectionChange(selectedManifests)
    }, [selectedManifests, onSelectionChange])
    const allManifestKeys = React.useMemo(
        () => manifests.map(getManifestKey),
        [manifests, getManifestKey]
    )
    const hasManifests = allManifestKeys.length > 0
    const selectedCount = selectedManifestKeys.length
    const isAllSelected = hasManifests && selectedCount === allManifestKeys.length
    const isIndeterminate = hasManifests && selectedCount > 0 && !isAllSelected

    const headerCheckboxRef = React.useRef(/** @type {HTMLInputElement | null} */ (null))
    React.useEffect(() => {
        if (headerCheckboxRef.current) {
            headerCheckboxRef.current.indeterminate = isIndeterminate
        }
    }, [isIndeterminate])

    function toggleAllManifests(nextChecked) {
        setSelectedManifestKeys(nextChecked ? allManifestKeys : [])
    }

    function toggleManifest(key, nextChecked) {
        setSelectedManifestKeys((prev) => {
            const next = new Set(prev)
            if (nextChecked) {
                next.add(key)
            } else {
                next.delete(key)
            }
            return Array.from(next)
        })
    }

    const SELECT_CHECKBOX_STYLE = {
        width: "1rem",
        height: "1rem",
        marginTop: 0,
    }
    const HEADER_CLASS = "text-uppercase text-secondary"
    const HEADER_STYLE = { fontSize: "0.75rem", fontWeight: 500, lineHeight: 1.2 }
    const BASE_HEADER = { className: HEADER_CLASS, style: HEADER_STYLE }

    function makeHeader(key, label) {
        return {
            key,
            label,
            ...BASE_HEADER,
            minWidth: 60,
        }
    }

    const defaultColumns = [
        { key: "location", label: "Location", preferredWidth: 110, cellType: "text", value: (m) => m.location },
        { key: "manifestNumber", label: "Manifest #", preferredWidth: 120, cellType: "text", value: (m) => m.manifestNumber, cellClassName: "fw-semibold", cellStyle: { color: "#0f6170" } },
        { key: "numOrders", label: "# Orders", preferredWidth: 90, cellType: "text", value: (m) => String(m.numOrders ?? "") },
        { key: "status", label: "Status", minWidth: 130, preferredWidth: 130, cellType: "status", value: (m) => m.status },
        { key: "createdDate", label: "Created Date", preferredWidth: 120, cellType: "text", value: (m) => m.createdDate },
        { key: "transferredDate", label: "Transferred Date", preferredWidth: 150, cellType: "text", value: (m) => m.transferredDate },
        { key: "receivedDate", label: "Received Date", preferredWidth: 130, cellType: "text", value: (m) => m.receivedDate },
    ]

    const effectiveColumns = Array.isArray(columns) && columns.length ? columns : defaultColumns

    const headers = [
        {
            ...makeHeader(
                "select",
                <input
                    ref={headerCheckboxRef}
                    type="checkbox"
                    className="form-check-input align-middle"
                    style={SELECT_CHECKBOX_STYLE}
                    checked={isAllSelected}
                    onChange={(event) => toggleAllManifests(event.target.checked)}
                    onClick={(event) => event.stopPropagation()}
                />
            ),
            minWidth: 35,
            preferredWidth: 35,
        },
        ...effectiveColumns.map((col) => ({
            ...makeHeader(col.key, col.label),
            preferredWidth: col.preferredWidth,
            minWidth: col.minWidth ?? 60,
            className: col.headerClassName ?? HEADER_CLASS,
            style: { ...HEADER_STYLE, ...(col.headerStyle ?? {}) },
        })),
    ]

    function renderStatusCell(manifest) {
        const statusColor = manifestStatusColor?.(manifest.rawStatus ?? manifest.status)
        return renderComponentCell(
            statusColor ? (
                <Badge
                    style={{
                        color: statusColor,
                        borderColor: statusColor,
                        backgroundColor: hexToRgba(statusColor, 0.16),
                    }}
                >
                    {manifest.status}
                </Badge>
            ) : (
                <Badge style={getManifestStatusBadgeStyle(manifest.rawStatus ?? manifest.status)}>
                    {manifest.status}
                </Badge>
            )
        )
    }

    function renderManifestRow(manifest, index) {
        const manifestKey = getManifestKey(manifest, index)
        const isChecked = selectedManifestKeySet.has(manifestKey)
        return (
            <tr
                key={manifest.id}
                style={{ cursor: "pointer" }}
                onClick={() => onManifestClick(manifest)}
            >
                {renderComponentCell(
                    <input
                        type="checkbox"
                        className="form-check-input align-middle"
                        style={SELECT_CHECKBOX_STYLE}
                        checked={isChecked}
                        onChange={(event) => toggleManifest(manifestKey, event.target.checked)}
                        onClick={(event) => event.stopPropagation()}
                    />
                )}
                {effectiveColumns.map((col) => {
                    const cellType = col.cellType ?? "text"
                    if (cellType === "status") {
                        return (
                            <React.Fragment key={col.key}>
                                {renderStatusCell(manifest)}
                            </React.Fragment>
                        )
                    }

                    if (cellType === "custom" && typeof col.render === "function") {
                        return (
                            <React.Fragment key={col.key}>
                                {renderComponentCell(col.render(manifest))}
                            </React.Fragment>
                        )
                    }

                    const value = typeof col.value === "function" ? col.value(manifest) : manifest?.[col.key]
                    const className = `${col.cellClassName ?? "text-dark"}`
                    const style = { fontSize: "0.875rem", ...(col.cellStyle ?? {}) }
                    return (
                        <React.Fragment key={col.key}>
                            {renderTextCell(value, className, style)}
                        </React.Fragment>
                    )
                })}
            </tr>
        )
    }

    return (
        <TableWithPagination
            headers={headers}
            rows={manifests}
            renderRow={renderManifestRow}
            isLoadingInitial={isLoadingInitial}
            totalRows={totalRows}
            page={page}
            perPage={perPage}
            onPageChange={onPageChange}
            onPerPageChange={onPerPageChange}
            perPageSelection={perPageSelection}
            onFitPerPageChange={onFitPerPageChange}
            stretchFooterToBottom={true}
            showRefetchLoader={showRefetchLoader}
        />
    )
}

